import { useMutation, useQuery } from "@apollo/client";
import React from "react";
import { useParams } from "react-router-dom";
import {
  GetEnforcementIntentDetailData,
  GetEnforcementIntentDetailVars,
  GET_ENFORCEMENT_INTENT_DETAIL,
} from "common/graphql/queries/GetEnforcementIntentDetail";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import TransactionImageCarousel from "@app.automotus.io/components/Dashboard/TransactionDetail/TransactionImageCarousel";
import Grid from "@mui/material/Grid";
import SessionDetailsItem from "@app.automotus.io/components/SessionDetailsItem";
import { convertTimeToString } from "common/format";
import { getViolationTypeLabel } from "common/format/violationType";
import Container from "@mui/material/Container";
import CitationTransactionRateBreakdown from "@app.automotus.io/components/CitationTransactionRateBreakdown";
import ViolationPenalty from "./ViolationPenalty";
import NoticeStatusHeader from "./NoticeStatusHeader/NoticeStatusHeader";
import NoticeActionsHeader from "./ActionsHeader/ActionsHeader";
import {
  APPLY_ENFORCEMENT_INTENT_CORRECTION,
  ApplyEnforcementIntentCorrectionData,
  ApplyEnforcementIntentCorrectionVars,
  UPDATE_ENFORCEMENT_INTENT_VEHICLE_MAKE,
  UpdateEnforcementIntentVehicleMakeData,
  UpdateEnforcementIntentVehicleMakeVars,
} from "common/graphql";
import Box from "@mui/material/Box";
import VehicleMakeSessionDetailsItem, { VehicleMakeFormValues } from "./VehicleMakeSessionDetailsItem";
import VehicleSessionDetailsItem, { VehicleSessionDetailsItemFormValues } from "./VehicleSessionDetailsItem";
import { formatInTimeZone, utcToZonedTime } from "date-fns-tz";

export const Notice: React.FC = () => {
  const { id } = useParams<{ id: string }>();

  const { data, loading } = useQuery<GetEnforcementIntentDetailData, GetEnforcementIntentDetailVars>(
    GET_ENFORCEMENT_INTENT_DETAIL,
    {
      variables: { id: id || "" },
    },
  );

  const intent = data?.enforcementIntent;
  const session = intent?.sessions[0];

  const isEditEnabled = intent?.reviews.some((review) => review.status === "pending");

  const [updateMake] = useMutation<UpdateEnforcementIntentVehicleMakeData, UpdateEnforcementIntentVehicleMakeVars>(
    UPDATE_ENFORCEMENT_INTENT_VEHICLE_MAKE,
  );
  const handleEditVehicleMake = async ({ make: newMake }: VehicleMakeFormValues) => {
    if (intent && isEditEnabled) {
      await updateMake({
        variables: {
          id: id || "",
          make: newMake.toLowerCase(),
          // We use a regex to ensure that only the first instance of the make is replaced,
          // which is needed for cases where the default value of "other_other" is used for the model
          model: intent.vehicleModel.replace(new RegExp(intent.vehicleMake), newMake.toLowerCase()),
        },
      });
    }
  };

  const [applyCorrection] = useMutation<ApplyEnforcementIntentCorrectionData, ApplyEnforcementIntentCorrectionVars>(
    APPLY_ENFORCEMENT_INTENT_CORRECTION,
  );
  const handleEditLicensePlate = async ({
    licensePlateNumber,
    licensePlateRegion,
  }: VehicleSessionDetailsItemFormValues) => {
    if (intent && isEditEnabled) {
      await applyCorrection({
        variables: {
          intentId: id || "",
          licensePlateNumber,
          licensePlateRegion,
        },
      });
    }
  };

  // Display the license plate capture date in the local timezone
  const licensePlateCaptureDate =
    intent && session
      ? utcToZonedTime(session.licensePlateCapturedAt || session.startedAt, intent.timezone)
      : new Date(0);

  return (
    <Box>
      <Container maxWidth="lg">
        <Stack spacing={3}>
          <NoticeActionsHeader intent={intent} loading={loading} />
          <NoticeStatusHeader loading={loading} intent={intent} />
          <Paper sx={{ p: 3 }}>
            <TransactionImageCarousel
              loading={loading}
              height={404}
              parkStartDate={new Date(session?.transaction.sessionStartedAtLocal || 0)}
              parkEndDate={new Date(session?.transaction.sessionEndedAtLocal || 0)}
              licensePlateCaptureDate={licensePlateCaptureDate}
              parkStartImageSrc={session?.startImageUrl || ""}
              parkEndImageSrc={session?.endImageUrl || ""}
              licensePlateCaptureImageSrc={session?.licensePlateImageUrl || ""}
            />
          </Paper>
          <Paper sx={{ p: 3 }}>
            <Stack spacing={1}>
              <Typography variant={"h5"} fontWeight={400}>
                Notice Details
              </Typography>
              <Grid container rowSpacing={2}>
                <SessionDetailsItem
                  loading={loading}
                  title={"Violation Date"}
                  content={formatInTimeZone(new Date(session?.violationAt || 0), intent?.timezone || "UTC", "M/d/yyyy")}
                  skeletonWidth={104}
                />
                <SessionDetailsItem
                  loading={loading}
                  title={"Violation Time"}
                  content={`${convertTimeToString(
                    utcToZonedTime(session?.violationAt || 0, intent?.timezone || "UTC"),
                  )}`}
                  skeletonWidth={166}
                />
                <SessionDetailsItem
                  loading={loading}
                  title={"Location"}
                  content={session?.streetAddress || ""}
                  skeletonWidth={111}
                />
                <SessionDetailsItem
                  loading={loading}
                  title={"City"}
                  content={`${session?.transaction.addressCity}, ${session?.transaction.addressState}`}
                  skeletonWidth={112}
                />
                <VehicleSessionDetailsItem
                  loading={loading}
                  licensePlateNumber={intent?.licensePlateNumber || ""}
                  licensePlateRegion={intent?.licensePlateRegion || ""}
                  skeletonWidth={104}
                  onSubmitEdit={isEditEnabled ? handleEditLicensePlate : undefined}
                />
                <VehicleMakeSessionDetailsItem
                  loading={loading}
                  vehicleMake={intent?.vehicleMake || ""}
                  skeletonWidth={166}
                  onSubmitEdit={isEditEnabled ? handleEditVehicleMake : undefined}
                />
                <SessionDetailsItem
                  loading={loading}
                  title={"Violation Code"}
                  content={intent?.code || ""}
                  skeletonWidth={112}
                />
                <SessionDetailsItem
                  loading={loading}
                  title={"Violation Type"}
                  content={getViolationTypeLabel(intent?.violationType || "")}
                  skeletonWidth={112}
                />
                {intent?.roLookupSubmittedAt && intent?.officerName && (
                  <SessionDetailsItem
                    loading={loading}
                    title={"Issued By"}
                    content={intent.officerName}
                    skeletonWidth={112}
                  />
                )}
              </Grid>
            </Stack>
          </Paper>
          <CitationTransactionRateBreakdown
            loading={loading}
            policyRateDetails={session?.transaction.park?.policyRateDetails || []}
          />
          <ViolationPenalty intent={intent} loading={loading} />
        </Stack>
      </Container>
    </Box>
  );
};

export default Notice;
